/**
 * Where the token pair lives between page loads.
 *
 * `localStorage` is a deliberate, documented trade-off. It is readable by any
 * script running on this origin, so a cross-site-scripting bug would expose the
 * tokens. The alternative — httpOnly cookies — resists that, but requires the
 * API to issue cookies, reintroduces CSRF, and does not work for the mobile
 * clients this API is also meant to serve.
 *
 * What makes the trade-off acceptable is the rest of the design: access tokens
 * live 15 minutes, refresh tokens rotate on every use and are revoked on reuse,
 * so a stolen pair has a short and self-limiting life. If this application ever
 * renders untrusted HTML, revisit this decision first.
 */

const ACCESS_TOKEN_KEY = 'sms.accessToken';
const REFRESH_TOKEN_KEY = 'sms.refreshToken';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

/**
 * Storage can throw, not just return null: Safari's private mode and browsers
 * configured to block site data raise on access. Every read and write is
 * therefore guarded — a failure degrades to "not signed in", never a crash.
 */
function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Session simply will not survive a reload. Nothing else breaks.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

export const tokenStorage = {
  getAccessToken: (): string | null => safeRead(ACCESS_TOKEN_KEY),
  getRefreshToken: (): string | null => safeRead(REFRESH_TOKEN_KEY),

  save({ accessToken, refreshToken }: TokenPair): void {
    safeWrite(ACCESS_TOKEN_KEY, accessToken);
    safeWrite(REFRESH_TOKEN_KEY, refreshToken);
  },

  clear(): void {
    safeRemove(ACCESS_TOKEN_KEY);
    safeRemove(REFRESH_TOKEN_KEY);
  },

  hasSession(): boolean {
    return safeRead(ACCESS_TOKEN_KEY) !== null && safeRead(REFRESH_TOKEN_KEY) !== null;
  },
};
